import React, { useState, useEffect, createContext, useContext, ReactNode, useRef } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    nickname: string;
    phone: string;
    marketingOptIn: boolean;
  }) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: { nickname?: string; phone?: string }) => Promise<void>;
}

/* Initialise the context with an undefined value – the consumer will guard against it */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // INITIAL_SESSION은 한 번만 처리하기 위한 플래그
  const handledInitial = useRef(false);

  /** 세션 및 로컬 스토리지 완전 초기화 */
  const clearAuthState = async (skipSignOut = false) => {
    try {
      // skipSignOut이 false일 때만 Supabase signOut 호출
      if (!skipSignOut) {
        console.log('### Calling supabase.auth.signOut() from clearAuthState');
        await supabase.auth.signOut({ scope: 'local' });
      } else {
        console.log('### Skipping signOut, only clearing local state');
      }
      
      // 로컬 스토리지에서 Supabase 관련 데이터 모두 삭제
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // 세션 스토리지도 정리
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('sb-')) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Clear auth state error:', error);
    }
    
    setSession(null);
    setUser(null);
    setIsAdmin(false);
  };

  /** 관리자 권한 확인 */
  const checkAdminStatus = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', userId)
        .maybeSingle();

      setIsAdmin(data?.is_admin === true);
    } catch (error) {
      console.error('Failed to check admin status:', error);
      setIsAdmin(false);
    }
  };

  /** Load a user's profile from the `profiles` table – create one if missing */
  const loadUserProfile = async (authUser: User) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      // `PGRST116` is the "no rows" error – ignore it
      if (error && error.code !== 'PGRST116') {
        console.error('Profile fetch error:', error);
        return;
      }

      // If no profile exists, create a default one
      if (!profile) {
        await createUserProfile(authUser);
      }
      
      // 관리자 권한 확인
      await checkAdminStatus(authUser.id);
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  /** Upsert a profile row for the given Supabase Auth user */
  const createUserProfile = async (authUser: User) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert(
          {
            id: authUser.id,
            email: authUser.email,
            nickname:
              authUser.user_metadata?.nickname ||
              authUser.user_metadata?.full_name ||
              authUser.user_metadata?.name ||
              authUser.email?.split('@')[0] ||
              '',
            phone: authUser.user_metadata?.phone || '',
            marketing_opt_in: authUser.user_metadata?.marketing_opt_in || false,
            is_admin: false,
          },
          { onConflict: 'id' }
        );

      if (error) {
        console.error('Profile creation error:', error);
      }
    } catch (err) {
      console.error('Failed to create profile:', err);
    }
  };

  /** Load the initial session and subscribe to auth changes */
  useEffect(() => {
    let mounted = true;

    // Subscribe to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (!mounted) return;
      
      console.log('Auth state changed:', event, 'session =', currentSession ? 'exists' : 'null');
      
      // 1) 최초 세션 - 한 번만 처리
      if (event === 'INITIAL_SESSION') {
        if (handledInitial.current) {
          console.log('INITIAL_SESSION already handled, skipping...');
          return;
        }
        handledInitial.current = true;

        if (currentSession?.user) {
          console.log('Initial session found, setting user');
          setSession(currentSession);
          setUser(currentSession.user);
          await loadUserProfile(currentSession.user);
        } else {
          console.log('No initial session found');
          setSession(null);
          setUser(null);
          setIsAdmin(false);
        }
        setLoading(false);
        return;
      }

      // 2) 로그인 성공
      if (event === 'SIGNED_IN') {
        console.log('User signed in');
        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);
          await loadUserProfile(currentSession.user);
        }
        setLoading(false);
        return;
      }

      // 3) 토큰 갱신 - 기존 user 유지
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);
          await checkAdminStatus(currentSession.user.id);
        }
        // loading은 변경하지 않음 (이미 false 상태)
        return;
      }

      // 4) 로그아웃
      if (event === 'SIGNED_OUT') {
        console.log('User signed out');
        // SIGNED_OUT 이벤트에서는 이미 Supabase가 세션을 삭제했으므로
        // signOut을 다시 호출하지 않고 로컬 상태만 정리
        await clearAuthState(true);
        setLoading(false);
        return;
      }

      // 5) 사용자 정보 업데이트
      if (event === 'USER_UPDATED') {
        console.log('User updated');
        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);
          await checkAdminStatus(currentSession.user.id);
        }
        return;
      }
    });

    // 새로고침 후 첫 로딩 때 현재 세션 한 번 더 확인
    const initSession = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (error) {
          console.error('Session fetch error:', error);
          
          // Refresh Token 오류 처리
          if (
            error.message.includes('Refresh Token') ||
            error.message.includes('refresh_token') ||
            error.message.includes('Invalid token')
          ) {
            console.log('Invalid refresh token detected, clearing auth state...');
            // 토큰 오류 시에만 signOut 호출
            await clearAuthState(false);
          } else {
            // 다른 오류는 로컬 상태만 정리
            await clearAuthState(true);
          }
          
          if (!handledInitial.current) {
            handledInitial.current = true;
            setSession(null);
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
          }
          return;
        }
        
        // INITIAL_SESSION 이벤트가 아직 처리되지 않았을 때만 설정
        if (!handledInitial.current) {
          handledInitial.current = true;
          
          if (initialSession?.user) {
            console.log('getSession: session found, setting user');
            setSession(initialSession);
            setUser(initialSession.user);
            await loadUserProfile(initialSession.user);
          } else {
            console.log('getSession: no session found');
            setSession(null);
            setUser(null);
            setIsAdmin(false);
          }
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Failed to fetch initial session:', err);
        
        if (!mounted) return;
        
        // 모든 인증 관련 오류에 대해 상태 초기화
        if (!handledInitial.current) {
          handledInitial.current = true;
          // 일반 오류는 로컬 상태만 정리
          await clearAuthState(true);
          setLoading(false);
        }
      }
    };

    initSession();

    // Cleanup subscription on unmount
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  /** Email / password login */
  const login = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      // 로그인 성공 시 세션 설정
      if (data.session) {
        setSession(data.session);
        setUser(data.user);
        await checkAdminStatus(data.user.id);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /** Register a new user */
  const register = async ({
    email,
    password,
    nickname,
    phone,
    marketingOptIn,
  }: {
    email: string;
    password: string;
    nickname: string;
    phone: string;
    marketingOptIn: boolean;
  }) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nickname,
            phone,
            marketing_opt_in: marketingOptIn,
          },
        },
      });

      if (error) {
        throw error;
      }

      if (data?.user) {
        await createUserProfile(data.user);
      }
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  /** Sign‑out */
  const logout = async () => {
    try {
      console.log('### User manually logging out');
      await clearAuthState(false);
    } catch (error) {
      console.error('Logout error:', error);
      // 오류가 발생해도 로컬 상태는 초기화
      await clearAuthState(true);
    }
  };

  /** Update mutable profile fields */
  const updateProfile = async (data: { nickname?: string; phone?: string }) => {
    if (!user) {
      throw new Error('로그인이 필요합니다');
    }

    const { error } = await supabase
      .from('profiles')
      .update(data)
      .eq('id', user.id);

    if (error) {
      throw error;
    }
  };

  /* -----------------------------------------------------------------------
   * Provider return – using `React.createElement` instead of JSX prevents
   * the "Expected '>' but found 'value'" parsing error in environments that
   * treat the file as plain TypeScript (i.e., .ts). This maintains the same
   * behaviour while ensuring the code compiles correctly.
   * --------------------------------------------------------------------- */
  return React.createElement(
    AuthContext.Provider,
    {
      value: {
        user,
        session,
        loading,
        isAdmin,
        login,
        register,
        logout,
        updateProfile,
      },
    },
    children
  );
}

/** Hook for consuming the auth context */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}